import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import UserAnalyticsModal from "./UserAnalyticsModal";

export default function QuestionDetailPanel({ questionText }) {
	
	const [selectedUser, setSelectedUser] = useState(null);
	const [showUserModal, setShowUserModal] = useState(false);

    // Demo-safe dummy data
    const intervalScores = [
        { label: "Week 1", value: 62 },
        { label: "Week 2", value: 65 },
        { label: "Week 3", value: 68 },
        { label: "Week 4", value: 71 },
        { label: "Week 5", value: 74 }
    ];

    const avgScore = 68;

    const userScores = [
        {
            id: 1,
            name: "John Smith",
            intervals: [
                { label: "Week 1", value: 64, comment: "Good baseline" },
                { label: "Week 2", value: 66, comment: "Slight improvement" },
                { label: "Week 3", value: 69, comment: "IT upgrade helped" },
                { label: "Week 4", value: 72, comment: "Much happier" },
                { label: "Week 5", value: 75, comment: "Excellent" }
            ]
        },
        {
            id: 2,
            name: "Sarah Jones",
            intervals: [
                { label: "Week 1", value: 60, comment: "Needs support" },
                { label: "Week 2", value: 63, comment: "Some progress" },
                { label: "Week 3", value: 65, comment: "Better engagement" },
                { label: "Week 4", value: 66, comment: "Stable" },
                { label: "Week 5", value: 67, comment: "Slightly positive" }
            ]
        }
    ];

    

    useEffect(() => {
        if (userScores.length > 0) {
            setSelectedUser(userScores[0]);
        }
    }, []);

    const styles = {

        container: {
            padding: "16px",
            backgroundColor: "#fafafa",
            borderTop: "1px solid #ddd"
        },

        title: {
            fontSize: "13px",
            fontWeight: 600,
            marginBottom: "12px",
            color: "#333"
        },

        chartGrid: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr",
            gap: "16px"
        },

        panel: {
            backgroundColor: "#fff",
            borderRadius: "8px",
            padding: "12px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            display: "flex",
            flexDirection: "column"
        },

        panelTitle: {
            fontSize: "12px",
            fontWeight: 600,
            marginBottom: "8px",
            color: "#444"
        },

        barRow: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "6px"
        },

        barTrack: {
            flex: 1,
            height: "8px",
            backgroundColor: "#eee",
            borderRadius: "4px",
            overflow: "hidden"
        },

        barFill: {
            height: "100%",
            backgroundColor: "#4c9aff",
            borderRadius: "4px"
        },

        variancePositive: {
            backgroundColor: "#2e7d32"
        },

        varianceNegative: {
            backgroundColor: "#c62828"
        },

        userRow: {
            display: "flex",
            justifyContent: "space-between",
            fontSize: "12px",
            padding: "4px 0",
            cursor: "pointer",
            alignItems: "center"
        },

        userRowActive: {
            backgroundColor: "#e8f0fe",
            borderRadius: "4px",
            padding: "4px 6px"
        },

        userInfoIcon: {
            color: "#999",
            fontSize: "11px",
            marginLeft: "6px",
            cursor: "pointer"
        }
    };
	

    return (
        <div style={styles.container}>

            <div style={styles.title}>
                Detail for: {questionText}
            </div>

            <div style={styles.chartGrid}>

                {/* Score by Interval */}
                <div style={styles.panel}>
                    <div style={styles.panelTitle}>Score by Interval</div>
                    {intervalScores.map((item, index) => (
                        <div key={index} style={styles.barRow}>
                            <div style={{ width: "50px", fontSize: "11px" }}>{item.label}</div>
                            <div style={styles.barTrack}>
                                <div
                                    style={{
                                        ...styles.barFill,
                                        width: item.value + "%"
                                    }}
                                />
                            </div>
                            <div style={{ width: "30px", fontSize: "11px" }}>{item.value}</div>
                        </div>
                    ))}
                </div>

                {/* Variance vs Average */}
                <div style={styles.panel}>
                    <div style={styles.panelTitle}>Variance vs Average</div>
                    {intervalScores.map((item, index) => {
                        const variance = item.value - avgScore;
                        return (
                            <div key={index} style={styles.barRow}>
                                <div style={{ width: "50px", fontSize: "11px" }}>{item.label}</div>
                                <div style={styles.barTrack}>
                                    <div
                                        style={{
                                            ...styles.barFill,
                                            ...(variance >= 0
                                                ? styles.variancePositive
                                                : styles.varianceNegative),
                                            width: Math.abs(variance) * 4 + "%"
                                        }}
                                    />
                                </div>
                                <div style={{ width: "30px", fontSize: "11px" }}>
                                    {variance >= 0 ? "+" : ""}{variance}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Users */}
                <div style={styles.panel}>
                    <div style={styles.panelTitle}>Users</div>
                    {userScores.map(user => (
                        <div
                            key={user.id}
                            style={{
                                ...styles.userRow,
                                ...(selectedUser?.id === user.id ? styles.userRowActive : {})
                            }}
                            onClick={() => setSelectedUser(user)}
                        >
                            <span>{user.name}</span>
							<FontAwesomeIcon
							    icon={faInfoCircle}
							    style={{ cursor: "pointer", marginLeft: "8px", color: "#888" }}
							    onClick={function () {
							        setShowUserModal(true);
							    }}
							/>
                        </div>
                    ))}
                </div>

                {/* Selected User by Interval */}
                <div style={styles.panel}>
                    <div style={styles.panelTitle}>
                        {selectedUser ? selectedUser.name : "Select a user"}
                    </div>

                    {selectedUser && selectedUser.intervals.map((item, index) => {
                        const variance = item.value - avgScore;
                        return (
                            <div key={index} style={styles.barRow}>
                                <div style={{ width: "50px", fontSize: "11px" }}>{item.label}</div>
                                <div style={styles.barTrack}>
                                    <div
                                        style={{
                                            ...styles.barFill,
                                            width: item.value + "%",
                                            backgroundColor: "#6c8cff"
                                        }}
                                        title={`${item.value} (${variance >= 0 ? "+" : ""}${variance}) – ${item.comment}`}
                                    />
                                </div>
                                <div style={{ width: "40px", fontSize: "11px" }}>
                                    {item.value} ({variance >= 0 ? "+" : ""}{variance})
                                </div>
                            </div>
                        );
                    })}
                </div>

            </div>
			<UserAnalyticsModal
			    user={selectedUser}
				showModal={showUserModal}
				    onClose={function () {
				        setShowUserModal(false);
				    }}
			/>
        </div>
    );
}
